import React from 'react';
import { FlexibleTextContent } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { RichText } from '@mutiny-pkg/champaign-schemas/src/schemas/richText/interfaces';
import { RichInput, type WithMentionItems, RichInputHandle } from '../RichInput/RichInput';
import { richTextToFlexibleText } from '../flexibleTextAst';

type RichInputProps = React.ComponentProps<typeof RichInput>;

type BaseInputProps = Omit<
  RichInputProps,
  'onChange' | 'onBlur' | 'onFocus' | 'mentionItems' | 'onMentionMenuOpen' | 'mentionItemsEmptyMessage'
>;

type FlexibleCallbacks = {
  onChange?: (value: FlexibleTextContent) => void;
  onBlur?: (ev: Event, value: FlexibleTextContent) => void;
  onFocus?: (ev: Event, value: FlexibleTextContent) => void;
};

type FlexibleTextInputProps = BaseInputProps & FlexibleCallbacks & WithMentionItems;

const FlexibleTextInput = React.forwardRef<RichInputHandle, FlexibleTextInputProps>(function FlexibleTextInput(
  { onChange, onBlur, onFocus, mentionItems, onMentionMenuOpen, mentionItemsEmptyMessage, ...rest },
  ref,
) {
  const handleChange = React.useCallback(
    (richText: RichText) => {
      onChange?.(richTextToFlexibleText(richText));
    },
    [onChange],
  );

  const handleBlur = React.useCallback(
    (ev: Event, richText: RichText) => {
      onBlur?.(ev, richTextToFlexibleText(richText));
    },
    [onBlur],
  );

  const handleFocus = React.useCallback(
    (ev: Event, richText: RichText) => {
      onFocus?.(ev, richTextToFlexibleText(richText));
    },
    [onFocus],
  );

  if (mentionItems !== undefined) {
    return (
      <RichInput
        ref={ref}
        mentionItems={mentionItems}
        onMentionMenuOpen={onMentionMenuOpen}
        mentionItemsEmptyMessage={mentionItemsEmptyMessage}
        onChange={onChange ? handleChange : undefined}
        onBlur={onBlur ? handleBlur : undefined}
        onFocus={onFocus ? handleFocus : undefined}
        {...rest}
      />
    );
  }

  return (
    <RichInput
      ref={ref}
      onChange={onChange ? handleChange : undefined}
      onBlur={onBlur ? handleBlur : undefined}
      onFocus={onFocus ? handleFocus : undefined}
      {...rest}
    />
  );
});

FlexibleTextInput.displayName = 'FlexibleTextInput';

export { FlexibleTextInput };
export default FlexibleTextInput;
